import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { getReferralStats } from "../lib/api";
import { formatCurrency, formatDate } from "../lib/utils";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Gift,
  Users,
  DollarSign,
  Copy,
  CheckCircle,
  Share2,
  Link as LinkIcon,
  TrendingUp,
} from "lucide-react";

export default function Referral() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const referralLink = `${window.location.origin}/register?ref=${user?.referral_code || ""}`;

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await getReferralStats();
      setStats(response.data);
    } catch (error) {
      console.error("Failed to load referral stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(user?.referral_code || "");
    setCopied(true);
    toast.success("Referral code copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(referralLink);
    toast.success("Referral link copied!");
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: "Join Lucky 7 Casino",
        text: `Join me on Lucky 7 Casino and get $${stats?.signup_bonus || 5} bonus! Use my referral code: ${user?.referral_code}`,
        url: referralLink,
      });
    } else {
      handleCopyLink();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-heading font-bold mb-2 flex items-center gap-3">
            <Gift className="w-8 h-8 text-secondary" />
            Referral Program
          </h1>
          <p className="text-muted-foreground">
            Invite friends and earn rewards for every player you refer
          </p>
        </motion.div>

        {/* Stats Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8"
        >
          <div className="glass-card rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-5 h-5 text-primary" />
              <span className="text-sm text-muted-foreground">Total Referrals</span>
            </div>
            <div className="text-3xl font-heading font-bold text-primary">
              {stats?.total_referrals || 0}
            </div>
          </div>

          <div className="glass-card rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <DollarSign className="w-5 h-5 text-secondary" />
              <span className="text-sm text-muted-foreground">Total Earnings</span>
            </div>
            <div className="text-3xl font-heading font-bold text-secondary">
              {formatCurrency(stats?.total_earnings || 0)}
            </div>
          </div>

          <div className="glass-card rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp className="w-5 h-5 text-accent" />
              <span className="text-sm text-muted-foreground">Play Commission</span>
            </div>
            <div className="text-3xl font-heading font-bold text-accent">
              {stats?.play_percentage || 0.5}%
            </div>
          </div>
        </motion.div>

        {/* Referral Code Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="glass-card rounded-2xl p-8 mb-8"
        >
          <h2 className="font-heading font-bold text-xl mb-6">Your Referral Code</h2>

          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="flex-1">
              <div className="relative">
                <Input
                  value={user?.referral_code || ""}
                  readOnly
                  className="bg-background/50 border-primary/30 h-14 text-2xl font-mono font-bold text-center tracking-widest text-primary"
                  data-testid="referral-code"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-2 top-1/2 -translate-y-1/2"
                  onClick={handleCopyCode}
                >
                  {copied ? (
                    <CheckCircle className="w-5 h-5 text-primary" />
                  ) : (
                    <Copy className="w-5 h-5" />
                  )}
                </Button>
              </div>
            </div>
            <Button
              onClick={handleShare}
              className="neon-glow font-heading font-bold uppercase h-14"
              data-testid="share-btn"
            >
              <Share2 className="w-5 h-5 mr-2" /> Share
            </Button>
          </div>

          <div className="space-y-2">
            <label className="text-sm text-muted-foreground flex items-center gap-2">
              <LinkIcon className="w-4 h-4" /> Your Referral Link
            </label>
            <div className="flex gap-2">
              <Input
                value={referralLink}
                readOnly
                className="bg-background/50 border-white/10 font-mono text-sm"
                data-testid="referral-link"
              />
              <Button variant="outline" onClick={handleCopyLink}>
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </motion.div>

        {/* How It Works */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="glass-card rounded-2xl p-8 mb-8"
        >
          <h2 className="font-heading font-bold text-xl mb-6">How It Works</h2>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
                <span className="text-xl font-heading font-bold text-primary">1</span>
              </div>
              <h3 className="font-bold mb-2">Share Your Code</h3>
              <p className="text-sm text-muted-foreground">
                Share your unique referral code or link with friends
              </p>
            </div>

            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-secondary/20 flex items-center justify-center mx-auto mb-4">
                <span className="text-xl font-heading font-bold text-secondary">2</span>
              </div>
              <h3 className="font-bold mb-2">Friends Sign Up</h3>
              <p className="text-sm text-muted-foreground">
                They get ${stats?.signup_bonus || 5} bonus, you get ${stats?.referrer_bonus || 10}
              </p>
            </div>

            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center mx-auto mb-4">
                <span className="text-xl font-heading font-bold text-accent">3</span>
              </div>
              <h3 className="font-bold mb-2">Earn Forever</h3>
              <p className="text-sm text-muted-foreground">
                Earn {stats?.play_percentage || 0.5}% of all their wagers, forever!
              </p>
            </div>
          </div>

          <div className="mt-8 p-4 rounded-lg bg-primary/10 border border-primary/30">
            <div className="flex items-center gap-3">
              <Gift className="w-5 h-5 text-primary" />
              <div>
                <div className="font-medium">Rewards Summary</div>
                <div className="text-sm text-muted-foreground">
                  New user gets <span className="text-primary font-bold">${stats?.signup_bonus || 5}</span> •
                  You get <span className="text-primary font-bold">${stats?.referrer_bonus || 10}</span> per signup +
                  <span className="text-primary font-bold"> {stats?.play_percentage || 0.5}%</span> of their bets
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Referred Users */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="glass-card rounded-2xl p-8"
        >
          <h2 className="font-heading font-bold text-xl mb-6">Your Referrals</h2>

          {!stats?.referred_users || stats.referred_users.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No referrals yet. Share your code to start earning!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {stats.referred_users.map((referral, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className="flex items-center justify-between p-4 rounded-lg bg-background/50 border border-white/5"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                      <Users className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <div className="font-medium">{referral.username}</div>
                      <div className="text-sm text-muted-foreground">
                        Joined {referral.joined ? formatDate(referral.joined) : "Recently"}
                      </div>
                    </div>
                  </div>
                  <div className="text-primary font-mono font-bold">
                    +${stats.referrer_bonus || 10}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
